import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, getLanguageFromUrl } from '../../utils/languageUtils';
import { getOptionDisplayName } from '../../utils/productOptionsUtils';
import { getGlobalSelectedOptions, setGlobalSelectedOptions } from '../../utils/globalOptions';
import './ProductTab.css';

const ProductTab = ({ product, onProductSelect, konvaController, configData }) => {
  const { t } = useTranslation();
  // Build an ordered list of available options (Option1..OptionN)
  const optionsList = useMemo(() => {
    const opts = configData?.SupportedModels?.Options || {};
    // keep keys sorted by the trailing number if present (Option1, Option2, ...)
    const entries = Object.entries(opts)
      .filter(([, def]) => def && Array.isArray(def.values) && def.values.length > 0)
      .sort(([a], [b]) => {
        const na = parseInt(String(a).replace(/[^0-9]/g, ''), 10) || 0;
        const nb = parseInt(String(b).replace(/[^0-9]/g, ''), 10) || 0;
        return na - nb;
      });
    return entries; // [ ["Option1", def], ["Option2", def], ... ]
  }, [configData]);
  
  // Use state object to track selected option raw values keyed by OptionX
  const [selectedOptions, setSelectedOptions] = useState({});



  // Helper function to get display name in current language
  const getDisplayName = (displayName) => {
    if (!displayName) return '';
    if (typeof displayName === 'string') {
      return displayName; // Backward compatibility
    }
    if (typeof displayName === 'object') {
      const currentLang = getLanguageFromUrl();
      return displayName[currentLang] || displayName['en'] || displayName['cn'] || '';
    }
    return '';
  };

  // Track the previous product ID to avoid resetting options when only the view changes
  const [prevProductId, setPrevProductId] = useState('');
  
  // Set initial options when component mounts or product changes (but not when only view changes)
  useEffect(() => {
    if (!product) return;
    
    // Get the current product ID
    const currentProductId = product.ProductionName || product.id || '';
    
    // Only update product selection if it's a new product
    if (onProductSelect && prevProductId !== currentProductId) {
      onProductSelect(product);
      setPrevProductId(currentProductId);
    }
    
    // Check if konvaController already has options set
    const currentOptions = konvaController?.getCurrentOptions?.() || {};
    // Also check global options
    const globalOptions = getGlobalSelectedOptions();
    
    
    // Only initialize options if:
    // 1. This is a new product (different ID), or
    // 2. We don't have options set yet
    if (prevProductId !== currentProductId || Object.keys(selectedOptions).length === 0) {
      
      // Initialize options from controller/global/product/config defaults for ALL defined options
      const newOptions = { ...selectedOptions };
      for (const [optKey, def] of optionsList) {
        // Priority: controller > global > product defaults (legacy) > first config value
        if (currentOptions?.[optKey]?.value !== undefined) {
          newOptions[optKey] = currentOptions[optKey].value;
        } else if (typeof currentOptions?.[optKey] === 'string') {
          newOptions[optKey] = currentOptions[optKey];
        } else if (globalOptions?.[optKey]?.value !== undefined) {
          newOptions[optKey] = globalOptions[optKey].value;
        } else if (typeof globalOptions?.[optKey] === 'string') {
          newOptions[optKey] = globalOptions[optKey];
        } else if (product?.Options?.[optKey]) {
          newOptions[optKey] = product.Options[optKey];
        } else if (Array.isArray(def?.values) && def.values[0]) {
          newOptions[optKey] = def.values[0].value;
        }
      }
      setSelectedOptions(newOptions);
    } else {
      
    }
    
    // Redraw the canvas when product changes
    if (konvaController?.redraw && typeof konvaController.redraw === 'function') {
      konvaController.redraw();
    }
  
  }, [product, onProductSelect, konvaController, configData, selectedOptions, prevProductId]);

  // Update product with selected options using current state
  const updateProductWithOptions = () => {
    if (!product || !konvaController) return;
    
    
    updateProductWithOptionsDirectly(selectedOptions);
  };
  
  // Update product with explicitly provided options
  // This avoids React state timing issues
  const updateProductWithOptionsDirectly = (options) => {
    if (!product || !konvaController) return;
    
    
    
    // Determine selected view: preserve current if possible, otherwise first available
    const currentView = konvaController.selectedView;
    const availableViews = product.Views || product.views || [];
    
    // Helper to extract view id
    const getViewId = (v) => {
      if (!v) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'object' && v.id) return v.id;
      return String(v);
    };
    
    // Resolve to a full view object
    let defaultView = null;
    if (currentView) {
      const currId = getViewId(currentView);
      const match = availableViews.find(v => getViewId(v) === currId);
      defaultView = match || (availableViews.length > 0 ? availableViews[0] : null);
    } else {
      defaultView = availableViews.length > 0 ? availableViews[0] : null;
    }
    
    
    
    
    // Ensure we have valid values for required parameters
    const productId = product.ProductionName || 'default-product';
    const baseUrl = (configData && configData.BaseProductUrl) || product.BaseProductUrl || '';
    
    // Only call loadProduct if we have valid required parameters
    if (productId && baseUrl) {
      // Build options object dynamically: each OptionX becomes { value }
      const optionsObject = {};
      for (const [optKey] of optionsList) {
        const raw = options?.[optKey];
        if (raw !== undefined && raw !== null && raw !== '') {
          optionsObject[optKey] = { value: raw };
        }
      }
      
      
      
      // Set global options first, then load product
      // loadProduct will automatically use global options\
      
      setGlobalSelectedOptions(optionsObject);
      konvaController.loadProduct(
        productId,
        baseUrl,
        product.hasDesign || false,
        product.DesignUrl || null,
        defaultView,
        product.CaseUrl || configData?.CaseUrl || null
      );
      
      // Use redraw only if it exists as a function
      if (konvaController?.redraw && typeof konvaController.redraw === 'function') {
        konvaController.redraw();
      }
    }
  };

  // Generic option selection handler
  const handleOptionSelect = (optionKey, value) => {
    
    
    // Create the new state object first to ensure we use the correct values
    const newOptions = {
      ...selectedOptions,
      [optionKey]: value
    };
    
    // Update the state
    setSelectedOptions(newOptions);
    
    // Call updateProductWithOptions directly with the new options
    // instead of relying on state update
    updateProductWithOptionsDirectly(newOptions);
  };

  if (!product) {
    return (
      <div className="error">
        <p>{t('product.noData', 'No product data available')}</p>
      </div>
    );
  }

  return (
    <div className="product-tab">      
      <div className="selection-details">
          {/* Render options that have more than one selectable value; single-value options are auto-selected but hidden */}
          {optionsList
            .filter(([, def]) => Array.isArray(def?.values) && def.values.length > 1)
            .map(([optKey, def]) => (
            <div className="option-selection" key={optKey}>
              <p className="option-title">
                <strong>{getOptionDisplayName(def.displayName)}</strong>
              </p>
              <div className="option-values">
                {def.values.map((option) => (
                  <button
                    key={`${optKey}:${option.value}`}
                    className={`option-value ${selectedOptions[optKey] === option.value ? 'selected' : ''}`}
                    onClick={() => handleOptionSelect(optKey, option.value)}
                  >
                    {getOptionDisplayName(option.displayName)}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
    </div>
  );
};

export default ProductTab;
